/**
 * Solicitar acceso al portal (Sprint 13 S13-22 — rediseño visual).
 *
 * Vive bajo PublicLayout (dark theme). Replica el patron visual del
 * Login: escudo grande + marca CALBUCURA + Card con form + Button
 * del proyecto. Antes usaba un esquema claro propio que no calzaba
 * con el resto del portal.
 */
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api/client';
import { crearSolicitudAcceso } from '../lib/api/solicitudes-acceso';
import type { VinculoDeclarado } from '../lib/api/types';
import { TurnstileWidget } from '../lib/captcha/TurnstileWidget';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { InputRut, type RutValidez } from '../components/ui/InputRut';
import { InputFecha } from '../components/ui/InputFecha';
import { celularCanonico, formatearCelularEnVivo } from '../lib/format';
import { hoy } from '../lib/tiempo';

const VINCULOS: { valor: VinculoDeclarado; etiqueta: string }[] = [
  { valor: 'propietario', etiqueta: 'Propietario' },
  { valor: 'arrendatario', etiqueta: 'Arrendatario' },
  { valor: 'familiar_propietario', etiqueta: 'Familiar de propietario' },
  { valor: 'otro', etiqueta: 'Otro' },
];

type Estado = 'idle' | 'enviando' | 'enviada' | 'error';

const INPUT_CLASS =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 text-text-primary px-3 py-2 focus:border-orange focus:ring-1 focus:ring-orange/30 placeholder:text-text-muted disabled:opacity-50';

export function SolicitarAcceso() {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const [nombre, setNombre] = useState('');
  const [rut, setRut] = useState('');
  const [rutValidez, setRutValidez] = useState<RutValidez>({
    valid: false,
    canonico: null,
  });
  const [lote, setLote] = useState('');
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [vinculo, setVinculo] = useState<VinculoDeclarado>('propietario');
  // Sprint 17 Z21: el solicitante informa desde cuando habita/posee el
  // inmueble. La comision puede ajustar al aprobar. Default vacio para
  // forzar entrada explicita.
  const [tenenciaDesde, setTenenciaDesde] = useState('');
  const [comentario, setComentario] = useState('');
  const [token, setToken] = useState<string | null>(null);

  const [estado, setEstado] = useState<Estado>('idle');
  const [mensajeError, setMensajeError] = useState<string | null>(null);

  function mensajeDeError(err: unknown): string {
    if (!(err instanceof ApiError)) {
      return 'No pudimos enviar tu solicitud. Intenta nuevamente en unos minutos.';
    }
    if (err.status === 503) {
      return 'El servicio de validación no está disponible en este momento. Intenta más tarde.';
    }
    if (err.status === 400) {
      return 'El captcha no validó. Recarga la página y completa el desafío de nuevo.';
    }
    if (err.status === 422) {
      return 'Algún dato es incorrecto. Revisa los campos y vuelve a enviar.';
    }
    // 409: ya tiene cuenta, o ya hay una solicitud en revisión. El backend
    // manda un texto que explica qué hacer en cada caso, así que se pasa
    // tal cual en vez de reemplazarlo por un genérico — es justamente la
    // información que evita que la persona vuelva a enviar el formulario.
    if (err.status === 409) {
      const detalle =
        typeof err.body === 'object' &&
        err.body !== null &&
        'detail' in err.body
          ? String((err.body as { detail: unknown }).detail)
          : '';
      return (
        detalle ||
        'Ya tienes una solicitud registrada con este RUT. La Comisión te responderá por correo.'
      );
    }
    return `No pudimos enviar tu solicitud (error ${err.status}). Intenta más tarde.`;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) {
      setMensajeError('Completa el captcha antes de enviar.');
      return;
    }
    if (!rutValidez.valid) {
      setMensajeError('El RUT no es válido. Revisa el dígito verificador.');
      return;
    }
    setEstado('enviando');
    setMensajeError(null);
    try {
      // Celular: si el usuario escribio algo, intentamos extraer los 9
      // digitos canonicos. Si no calza, enviamos null (es opcional) —
      // el backend tolera y la comision verifica al aprobar.
      const celular = celularCanonico(telefono);
      await crearSolicitudAcceso({
        nombre_completo: nombre.trim(),
        rut: rutValidez.canonico ?? rut.trim(),
        lote_solicitado: lote.trim(),
        email: email.trim().toLowerCase(),
        telefono: celular,
        vinculo_declarado: vinculo,
        tenencia_desde: tenenciaDesde,
        comentario: comentario.trim() || null,
        turnstile_token: token,
      });
      setEstado('enviada');
    } catch (err) {
      setMensajeError(mensajeDeError(err));
      setEstado('error');
      setToken(null);
    }
  }

  if (estado === 'enviada') {
    return (
      <div className="w-full max-w-md flex flex-col items-center">
        <img
          src="/escudo-calbucura-original.png"
          alt="Escudo Calbucura"
          className="h-20 w-auto mb-4"
        />
        <h1 className="text-3xl font-medium text-text-primary tracking-widest mb-1">
          CALBUCURA
        </h1>
        <p className="text-sm text-text-secondary mb-8">Portal de vecinos</p>

        <Card className="w-full">
          <div className="bg-state-success/15 border-l-4 border-state-success p-4 rounded-sm">
            <p className="text-sm text-text-primary font-medium">
              Solicitud enviada.
            </p>
            <p className="text-sm text-text-secondary mt-2">
              La Comisión la revisará y, si se aprueba, recibirás un correo de
              bienvenida con el enlace para entrar al portal.
            </p>
          </div>
        </Card>

        <p className="text-sm text-text-secondary mt-6 text-center">
          <Link to="/login" className="text-orange hover:underline font-medium">
            Ir al inicio de sesión
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md flex flex-col items-center">
      <img
        src="/escudo-calbucura-original.png"
        alt="Escudo Calbucura"
        className="h-20 w-auto mb-4"
      />
      <h1 className="text-3xl font-medium text-text-primary tracking-widest mb-1">
        CALBUCURA
      </h1>
      <p className="text-sm text-text-secondary mb-8">Portal de vecinos</p>

      <Card className="w-full">
        <h2 className="text-xl font-medium text-orange mb-2">
          Solicitar acceso al portal
        </h2>
        <p className="text-sm text-text-secondary mb-6">
          Completa el formulario y la Comisión revisará tu solicitud. Si se
          aprueba, recibirás un correo con el enlace para entrar.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="nombre"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Nombre completo
            </label>
            <input
              id="nombre"
              type="text"
              required
              maxLength={200}
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={estado === 'enviando'}
              className={INPUT_CLASS}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="rut"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                RUT
              </label>
              <InputRut
                id="rut"
                value={rut}
                onChange={(f, v) => {
                  setRut(f);
                  setRutValidez(v);
                }}
                disabled={estado === 'enviando'}
              />
            </div>
            <div>
              <label
                htmlFor="lote"
                className="block text-sm font-medium text-text-primary mb-1"
              >
                Casa
              </label>
              <input
                id="lote"
                type="text"
                required
                maxLength={50}
                value={lote}
                onChange={(e) => setLote(e.target.value)}
                disabled={estado === 'enviando'}
                className={INPUT_CLASS}
                placeholder="Ej: 6931"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={estado === 'enviando'}
              className={INPUT_CLASS}
              placeholder="tu@correo.cl"
            />
          </div>

          <div>
            <label
              htmlFor="telefono"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Teléfono{' '}
              <span className="text-text-muted font-normal">(opcional)</span>
            </label>
            <div className="flex items-stretch">
              <span
                className="inline-flex items-center px-3 rounded-l-sm bg-bg-elevated border-[0.5px] border-r-0 border-white/15 text-text-muted text-sm select-none"
                aria-hidden="true"
              >
                +56
              </span>
              <input
                id="telefono"
                type="tel"
                inputMode="numeric"
                maxLength={13}
                value={telefono}
                onChange={(e) =>
                  setTelefono(formatearCelularEnVivo(e.target.value))
                }
                disabled={estado === 'enviando'}
                className={`${INPUT_CLASS} rounded-l-none`}
                placeholder="9 1234 5678"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="tenencia_desde"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              ¿Desde cuándo vives o eres dueño?
            </label>
            <InputFecha
              id="tenencia_desde"
              requerido
              value={tenenciaDesde}
              onChange={setTenenciaDesde}
              disabled={estado === 'enviando'}
              max={hoy()}
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <label
              htmlFor="vinculo"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Vínculo con la casa
            </label>
            <select
              id="vinculo"
              value={vinculo}
              onChange={(e) => setVinculo(e.target.value as VinculoDeclarado)}
              disabled={estado === 'enviando'}
              className={INPUT_CLASS}
            >
              {VINCULOS.map((v) => (
                <option
                  key={v.valor}
                  value={v.valor}
                  className="bg-bg-sunken text-text-primary"
                >
                  {v.etiqueta}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="comentario"
              className="block text-sm font-medium text-text-primary mb-1"
            >
              Comentario{' '}
              <span className="text-text-muted font-normal">(opcional)</span>
            </label>
            <textarea
              id="comentario"
              maxLength={500}
              rows={2}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              disabled={estado === 'enviando'}
              className={INPUT_CLASS}
              placeholder="Cualquier información que ayude a la Comisión a validar tu solicitud."
            />
          </div>

          <div className="flex justify-center pt-2">
            <TurnstileWidget siteKey={siteKey} onToken={setToken} />
          </div>

          {mensajeError && (
            <div
              className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
              role="alert"
            >
              {mensajeError}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={estado === 'enviando' || !token}
          >
            {estado === 'enviando' ? 'Enviando…' : 'Enviar solicitud'}
          </Button>

          <p className="text-center text-xs text-text-secondary pt-2">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-orange hover:underline">
              Inicia sesión
            </Link>
          </p>
        </form>
      </Card>

      <p className="text-center text-xs text-text-muted mt-6">
        Comisión Barrio Calbucura
      </p>
    </div>
  );
}
